/**
 * Scroll restoration per-path.
 *
 * - On mount: reads sessionStorage["scroll:{pathname}"] and restores scrollTop.
 * - Before unmount (i.e. when the user navigates away): saves current scrollTop.
 * - Uses window scroll (main viewport). If you have a scrollable inner container,
 *   pass a ref to the element.
 *
 * Also mounts a top-level component `<ScrollRestorer />` in App that hooks the
 * router: on POP we restore, on PUSH we reset to 0.
 */
import { useEffect } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

const KEY = (path) => `pxos.scroll:${path}`;

export function useScrollRestoration() {
  const loc = useLocation();
  const navType = useNavigationType();

  useEffect(() => {
    const key = KEY(loc.pathname + loc.search);
    // Save on unmount / route change
    const save = () => {
      try { sessionStorage.setItem(key, String(window.scrollY || 0)); } catch {}
    };

    if (navType === "POP") {
      // Back/forward → restore
      try {
        const y = parseInt(sessionStorage.getItem(key) || "0", 10);
        // Delay so React finishes layout
        window.requestAnimationFrame(() => window.scrollTo(0, y));
      } catch {}
    } else {
      // PUSH / REPLACE → scroll top (natural for a new screen)
      window.requestAnimationFrame(() => window.scrollTo(0, 0));
    }

    window.addEventListener("beforeunload", save);
    return () => {
      save();
      window.removeEventListener("beforeunload", save);
    };
  }, [loc.pathname, loc.search, navType]);
}

/**
 * Simple wrapper for App root: put <ScrollRestorer /> inside BrowserRouter.
 */
export default function ScrollRestorer() {
  useScrollRestoration();
  return null;
}
